import { GenericActivityListSort } from 'src/models/genericActivityList';
import { IdoDetailsSearchFieldLocation, IdoDetailsSearchFieldOperation } from 'src/models/idoList/detailsSearch';
import { AbstractQuery } from 'src/models/query';

export class GetTheIdoListviewModelAndDataQuery extends AbstractQuery {
	cacheable = true;
	name = 'IFS_IDO_Query_Queries_GetTheIdoListviewModelAndDataQuery';

	constructor(
		searchTerm: string,
		idoInstanceId: string,
		serializeIdoIdoListFilter: string,
		page: number,
		limit: number,
		sort: Array<GenericActivityListSort> = []
	) {
		super();

		this.parameters.push({ fulltextSearch: searchTerm });

		this.parameters.push({ idoInstanceId: idoInstanceId });

		this.parameters.push({ detailsSearch: serializeIdoIdoListFilter });

		this.page = page;
		this.limit = limit;
		this.sort = sort;
		this.disableSubscriptionsWhileSearchIsActive(searchTerm, serializeIdoIdoListFilter);
	}

	uniqueQueryIdentifier(): string {
		return this.createStableIdentifier();
	}
}

export class GetTheSharedQueriesForTheKanbanBoardQuery extends AbstractQuery {
	cacheable = true;
	name = 'IFS_Kanban_Query_Queries_GetTheSharedQueriesForTheKanbanBoardQuery';
	sort = [];
}

export class GetTheIdoInstancesQuery extends AbstractQuery {
	cacheable = true;
	name = 'IFS_IDO_Query_Queries_GetTheIdoInstancesQuery';
	sort = [];
	subscribeForUpdates = false;
}

export class GetTheIdoListviewFieldsForAnInstanceQuery extends AbstractQuery {
	cacheable = false;
	name = 'IFS_IDO_Query_Queries_GetTheIdoListviewFieldsForAnInstanceQuery';
	sort = [];
	subscribeForUpdates = false;

	constructor(idoInstanceId: string, fieldName?: string) {
		super();
		this.parameters.push({ idoInstanceId });
		if (fieldName !== undefined && fieldName !== '') {
			this.parameters.push({ fieldName });
		}
	}
}

export class GetTheIdoListviewFieldValuesForAnInstanceAndFieldQuery extends AbstractQuery {
	cacheable = false;
	name = 'IFS_IDO_Query_Queries_GetTheIdoListviewFieldValuesForAnInstanceAndFieldQuery';
	sort = [];
	subscribeForUpdates = false;

	constructor(idoInstanceId: string, name: string) {
		super();
		this.parameters.push({ idoInstanceId });
		this.parameters.push({ name });
	}
}

export interface GetTheIdoListviewFieldValueAutocompleteHintsForAnInstanceAndFieldQueryPayload {
	alwaysPresent: string;
	idoInstanceId: string;
	location: IdoDetailsSearchFieldLocation;
	name: string;
	operation: IdoDetailsSearchFieldOperation;
	searchValue: string;
}

export class GetTheIdoListviewFieldValueAutocompleteHintsForAnInstanceAndFieldQuery extends AbstractQuery {
	cacheable = false;
	ignoreCachedResult = true;
	name = 'IFS_IDO_Query_Queries_GetTheIdoListviewFieldValueAutocompleteHintsForAnInstanceAndFieldQuery';
	sort = [];
	subscribeForUpdates = false;

	constructor(payload: GetTheIdoListviewFieldValueAutocompleteHintsForAnInstanceAndFieldQueryPayload) {
		super();
		this.parameters.push({ idoInstanceId: payload.idoInstanceId });
		this.parameters.push({ name: payload.name });
		this.parameters.push({ operation: payload.operation });
		this.parameters.push({ location: payload.location });
		this.parameters.push({ searchValue: payload.searchValue });
		this.parameters.push({ alwaysPresent: payload.alwaysPresent });
	}
}

export class GetTheIdoListviewIdosInInstanceCountQuery extends AbstractQuery {
	cacheable = false;
	name = 'IFS_IDO_Query_Queries_GetTheIdoListviewIdosInInstanceCountQuery';
	sort = [];
	subscribeForUpdates = false;
}

export class GetTheIdoListviewSummaryForAnInstanceQuery extends AbstractQuery {
	cacheable = false;
	name = 'IFS_IDO_Query_Queries_GetTheIdoListviewSummaryForAnInstanceQuery';
	sort = [];
	subscribeForUpdates = false;

	constructor(searchTerm: string, idoInstanceId: string, serializeIdoIdoListFilter: string) {
		super();

		this.parameters.push({ fulltextSearch: searchTerm });
		this.parameters.push({ idoInstanceId: idoInstanceId });
		this.parameters.push({ detailsSearch: serializeIdoIdoListFilter });
	}
}

export type IdoCompleteExportType = 'combined' | 'contactPerson' | 'noteAndArchive' | 'standard';

abstract class AbstractIdoListCompleteExportQuery extends AbstractQuery {
	cacheable = false;

	constructor(searchTerm: string, idoInstanceId: string, serializeIdoIdoListFilter: string, idoInstanceName: string) {
		super();

		delete this.page;
		this.parameters.push({ fulltextSearch: searchTerm });
		this.parameters.push({ idoInstanceId: idoInstanceId });
		this.parameters.push({ detailsSearch: serializeIdoIdoListFilter });
		this.parameters.push({ idoInstanceName: idoInstanceName });
		this.subscribeForUpdates = false;
	}
}

class ExportTheIdoListViewForAnInstanceAsCSVQuery extends AbstractIdoListCompleteExportQuery {
	name = 'IFS_IDO_Query_Queries_ExportTheIdoListViewForAnInstanceAsCSVQuery';
}

class ExportTheIdoListViewContactPersonForAnInstanceAsCSVQuery extends AbstractIdoListCompleteExportQuery {
	name = 'IFS_IDO_Query_Queries_ExportTheIdoListViewContactPersonForAnInstanceAsCSVQuery';
}

class ExportTheIdoListViewCombinedForAnInstanceAsCSVQuery extends AbstractIdoListCompleteExportQuery {
	name = 'IFS_IDO_Query_Queries_ExportTheIdoListViewCombinedForAnInstanceAsCSVQuery';
}

class ExportTheIdoListViewNoteAndArchiveForAnInstanceAsCSVQuery extends AbstractIdoListCompleteExportQuery {
	name = 'IFS_IDO_Query_Queries_ExportTheIdoListViewNoteAndArchiveForAnInstanceAsCSVQuery';
}

export const createIdoListCompleteExportQuery = (
	exportType: IdoCompleteExportType,
	searchTerm: string,
	idoInstanceId: string,
	serializeIdoIdoListFilter: string,
	idoInstanceName: string
): AbstractIdoListCompleteExportQuery => {
	switch (exportType) {
		case 'combined':
			return new ExportTheIdoListViewCombinedForAnInstanceAsCSVQuery(
				searchTerm,
				idoInstanceId,
				serializeIdoIdoListFilter,
				idoInstanceName
			);
		case 'contactPerson':
			return new ExportTheIdoListViewContactPersonForAnInstanceAsCSVQuery(
				searchTerm,
				idoInstanceId,
				serializeIdoIdoListFilter,
				idoInstanceName
			);
		case 'noteAndArchive':
			return new ExportTheIdoListViewNoteAndArchiveForAnInstanceAsCSVQuery(
				searchTerm,
				idoInstanceId,
				serializeIdoIdoListFilter,
				idoInstanceName
			);
		case 'standard':
			return new ExportTheIdoListViewForAnInstanceAsCSVQuery(
				searchTerm,
				idoInstanceId,
				serializeIdoIdoListFilter,
				idoInstanceName
			);
	}
};
